export const exportComprehensiveSaleReportToPDF = (data) => {
  const {
    sales,
    payments,
    returns,
    balancePayments = [],
    summary,
    selectedSaleType = "all",
  } = data;

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object" && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(numStr) || 0;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSaleType = (sale) => {
    const items = Array.isArray(sale?.sale_items) ? sale.sale_items : [];
    if (!items.length) return "retail";

    const types = new Set(
      items.map((item) => {
        const salePrice = parseAmount(item?.sale_price);
        const wholesalePrice = parseAmount(item?.product_details?.wholesale_price);
        const retailPrice = parseAmount(
          item?.product_details?.retail_price ?? item?.product_details?.sale_price
        );

        if (wholesalePrice > 0 && salePrice === wholesalePrice) return "wholesale";
        if (retailPrice > 0 && salePrice === retailPrice) return "retail";
        if (wholesalePrice > 0 && retailPrice <= 0) return "wholesale";
        return "retail";
      })
    );

    return types.size === 1 ? Array.from(types)[0] : "mixed";
  };

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>📊 Comprehensive Sales Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.6;
            color: #102a43;
            background: #eef4fb;
            min-height: 100vh;
            padding: 20px;
          }
          
          .container {
            background: white;
            border-radius: 15px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 30px 20px;
            position: relative;
          }
          
          .header h1 { 
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          
          .content {
            padding: 30px;
          }
          
          .summary-grid { 
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .summary-card { 
            background: #ffffff;
            color: #102a43;
            padding: 25px;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
            position: relative;
            overflow: hidden;
            border: 1px solid #d9e2ec;
          }
          
          .summary-card.sales { background: #eef6ff; }
          .summary-card.payments { background: #edfdf3; }
          .summary-card.returns { background: #fff4f4; }
          .summary-card.outstanding { background: #fff8e6; color: #102a43; }
          .summary-card.net { background: #f4ecff; }
          
          .summary-card h3 { 
            font-size: 14px;
            margin-bottom: 10px;
            opacity: 0.9;
          }
          
          .summary-card .amount { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          
          .summary-card .count { 
            font-size: 12px;
            opacity: 0.8;
          }
          
          .section { 
            margin: 40px 0;
            page-break-inside: avoid;
          }
          
          .section-header {
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            padding: 15px 20px;
            border-radius: 8px 8px 0 0;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .table-container {
            background: white;
            border-radius: 0 0 8px 8px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          table { 
            width: 100%;
            border-collapse: collapse;
          }
          
          th { 
            background: #12344d;
            color: #ffffff;
            font-weight: 600;
            padding: 15px 12px;
            text-align: left;
            border-bottom: 2px solid #0f2f46;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td { 
            padding: 12px;
            border-bottom: 1px solid #f1f3f4;
            font-size: 11px;
            vertical-align: middle;
          }
          
          tr:nth-child(even) { 
            background: #fafbfc;
          }
          
          .summary-row { 
            background: linear-gradient(90deg, #667eea, #764ba2) !important;
            color: white !important;
            font-weight: 600;
          }
          
          .summary-row td {
            border-color: rgba(255,255,255,0.2);
            padding: 15px 12px;
          }
          
          .status-badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-paid { background: #d4edda; color: #155724; }
          .status-partial { background: #fff3cd; color: #856404; }
          .status-unpaid { background: #f8d7da; color: #721c24; }
          
          .amount-cell { 
            text-align: right;
            font-weight: 700;
            color: #102a43;
            font-family: 'Courier New', monospace;
          }
          
          .id-cell {
            font-weight: 600;
            color: #6c757d;
          }
          
          .footer { 
            margin-top: 50px;
            padding: 30px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 12px;
            text-align: center;
            color: #6c757d;
            border-top: 3px solid #667eea;
          }
          
          .footer-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .footer-stat {
            text-align: center;
          }
          
          .footer-stat .number {
            font-size: 18px;
            font-weight: 700;
            color: #495057;
            display: block;
          }
          
          .footer-stat .label {
            font-size: 11px;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          @media print {
            body { 
              background: white;
              padding: 0;
            }
            .container {
              box-shadow: none;
              border-radius: 0;
            }
            .section { 
              page-break-inside: avoid;
            }
            .summary-grid { 
              page-break-inside: avoid;
            }
          }
          
          @page {
            margin: 0.5in;
            size: A4;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 Comprehensive Sales Report</h1>
            <p>Generated on ${getCurrentDate()}</p>
            <p>Sale type filter: ${selectedSaleType}</p>
          </div>
          
          <div class="content">
            ${
              summary && Object.keys(summary).length > 0
                ? `
            <div class="summary-grid">
              ${
                summary.previous_balance != null &&
                Number(summary.previous_balance) !== 0
                  ? `
              <div class="summary-card" style="background: linear-gradient(135deg, #434343 0%, #000000 100%); color: white;">
                <h3>📌 Previous Balance (Opening)</h3>
                <div class="amount">${formatCurrency(
                  summary.previous_balance || 0,
                )} ${summary.previous_balance_currency || ""}</div>
                <div class="count">Customer opening balance</div>
              </div>
              `
                  : ""
              }
              <div class="summary-card sales">
                <h3>🛒 Total Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.total_sales?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_sales?.count || 0
                } transactions</div>
                <div class="count">Discount ${formatCurrency(
                  summary.total_sales?.discount_amount || 0,
                )}</div>
              </div>
              <div class="summary-card payments">
                <h3>💳 Revenue (Collected)</h3>
                <div class="amount">${formatCurrency(
                  summary.total_revenue ?? summary.total_paid ?? 0,
                )}</div>
                <div class="count">Sale + Balance payments</div>
              </div>
              <div class="summary-card outstanding">
                <h3>⚠️ Outstanding</h3>
                <div class="amount">${formatCurrency(
                  summary.outstanding_including_previous != null
                    ? summary.outstanding_including_previous
                    : summary.outstanding_amount || 0,
                )}</div>
                <div class="count">${
                  summary.outstanding_including_previous != null
                    ? summary.is_all_customers
                      ? "Opening balance + Sales (after returns)"
                      : "Previous + Sales (after returns)"
                    : "After returns"
                }</div>
              </div>
              <div class="summary-card net">
                <h3>🏷️ Wholesale Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.sale_type_breakdown?.wholesale?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.sale_type_breakdown?.wholesale?.count || 0
                } sales</div>
              </div>
              <div class="summary-card net">
                <h3>🛍️ Retail Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.sale_type_breakdown?.retail?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.sale_type_breakdown?.retail?.count || 0
                } sales</div>
              </div>
              <div class="summary-card returns">
                <h3>↩️ Total Returns</h3>
                <div class="amount">${formatCurrency(
                  summary.total_returns?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_returns?.count || 0
                } returns</div>
              </div>
              <div class="summary-card net">
                <h3>💰 Net Sales</h3>
                <div class="amount">${formatCurrency(
                  summary.net_sales || 0,
                )}</div>
                <div class="count">Final value</div>
              </div>
              ${
                (summary.total_balance_payments?.count ?? 0) > 0
                  ? `
              <div class="summary-card" style="background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); color: #2c3e50;">
                <h3>📋 Balance Payments</h3>
                <div class="amount">${formatCurrency(
                  summary.total_balance_payments?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_balance_payments?.count || 0
                } payments</div>
              </div>
              `
                  : ""
              }
            </div>
            `
                : ""
            }

            ${
              sales && sales.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                🛒 Sales Transactions (${sales.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 8%">ID</th>
                      <th style="width: 18%">Customer</th>
                      <th style="width: 12%">Date</th>
                      <th style="width: 10%">Type</th>
                      <th style="width: 12%">Total Amount</th>
                      <th style="width: 10%">Discount</th>
                      <th style="width: 11%">Amount Paid</th>
                      <th style="width: 11%">Outstanding</th>
                      <th style="width: 7%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${sales
                      .map((sale) => {
                        const saleReturns = returns.filter(
                          (r) => r.sale === sale.id,
                        );
                        const returnAmount = saleReturns.reduce(
                          (sum, r) => sum + parseAmount(r.total_refound_amount),
                          0,
                        );
                        const subtotalAmount = parseAmount(sale.total_amount);
                        const discountAmount = parseAmount(sale.discount || 0);
                        const totalAmount = parseAmount(
                          sale.effective_total_amount ?? sale.total_amount,
                        );
                        const paidAmount = parseAmount(sale.total_paid || 0);
                        const outstanding = Math.max(
                          0,
                          totalAmount - paidAmount - returnAmount,
                        );
                        const status =
                          outstanding <= 0
                            ? "Paid"
                            : paidAmount > 0
                              ? "Partial"
                              : "Unpaid";
                        const statusClass =
                          outstanding <= 0
                            ? "status-paid"
                            : paidAmount > 0
                              ? "status-partial"
                              : "status-unpaid";

                        return `
                        <tr>
                          <td class="id-cell">#${sale.id}</td>
                          <td>${
                            sale.customer_details?.name || "Walk-in Customer"
                          }</td>
                          <td>${formatDate(sale.sale_date)}</td>
                          <td style="text-transform: capitalize;">${getSaleType(sale)}</td>
                          <td class="amount-cell">${formatCurrency(
                            subtotalAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            discountAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            paidAmount,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            outstanding,
                          )}</td>
                          <td><span class="status-badge ${statusClass}">${status}</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>📊 TOTAL SUMMARY</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_sales?.amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_sales?.discount_amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_paid || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.outstanding_including_previous != null
                          ? summary.outstanding_including_previous
                          : summary.outstanding_amount || 0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              payments && payments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                💳 Payment Transactions (${payments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Payment ID</th>
                      <th style="width: 15%">Sale ID</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 25%">Payment Date</th>
                      <th style="width: 15%">Method</th>
                      <th style="width: 15%">Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${payments
                      .map(
                        (payment) => `
                      <tr>
                        <td class="id-cell">#${payment.id}</td>
                        <td>Sale #${payment.sale}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(payment.amount),
                        )}</td>
                        <td>${formatDate(payment.payment_date)}</td>
                        <td>${payment.payment_method || "Cash"}</td>
                        <td>${payment.currency_details?.title || "USD"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>💰 TOTAL SALE PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        payments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              balancePayments && balancePayments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                📋 Balance Payments (${balancePayments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">ID</th>
                      <th style="width: 25%">Customer</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 20%">Payment Date</th>
                      <th style="width: 15%">Currency</th>
                      <th style="width: 10%">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${balancePayments
                      .map(
                        (bp) => `
                      <tr>
                        <td class="id-cell">#${bp.id}</td>
                        <td>${bp.customer_details?.name || "-"}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(bp.amount),
                        )}</td>
                        <td>${formatDate(bp.payment_date)}</td>
                        <td>${bp.currency_details?.title || "-"}</td>
                        <td>${bp.notes || "-"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>📋 TOTAL BALANCE PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_balance_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        balancePayments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              returns && returns.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                ↩️ Return Transactions (${returns.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Return ID</th>
                      <th style="width: 15%">Sale ID</th>
                      <th style="width: 20%">Return Date</th>
                      <th style="width: 25%">Reason</th>
                      <th style="width: 15%">Refund Amount</th>
                      <th style="width: 10%">Items Count</th>
                      <th style="width: 5%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${returns
                      .map((returnItem) => {
                        const refundAmount = parseAmount(
                          returnItem.total_refound_amount || 0,
                        );
                        return `
                        <tr>
                          <td class="id-cell">#${returnItem.id}</td>
                          <td>Sale #${returnItem.sale}</td>
                          <td>${formatDate(returnItem.return_date)}</td>
                          <td>${returnItem.reason || "No reason provided"}</td>
                          <td class="amount-cell">${formatCurrency(
                            refundAmount,
                          )}</td>
                          <td style="text-align: center">${
                            returnItem.return_items?.length || 0
                          }</td>
                          <td><span class="status-badge status-paid">✓</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>🔄 TOTAL RETURNS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_returns?.amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${returns.reduce(
                        (sum, r) => sum + (r.return_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            <div class="footer">
              <div class="footer-stats">
                <div class="footer-stat">
                  <span class="number">${sales?.length || 0}</span>
                  <span class="label">Sales</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${payments?.length || 0}</span>
                  <span class="label">Sale Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${balancePayments?.length || 0}</span>
                  <span class="label">Balance Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.total_revenue ?? summary.total_paid ?? 0,
                  )}</span>
                  <span class="label">Revenue</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${returns?.length || 0}</span>
                  <span class="label">Returns</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.outstanding_including_previous != null
                      ? summary.outstanding_including_previous
                      : summary.outstanding_amount || 0,
                  )}</span>
                  <span class="label">Outstanding</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.net_sales || 0,
                  )}</span>
                  <span class="label">Net Sales</span>
                </div>
              </div>
              <p><strong>📊 Comprehensive Sales Report</strong> • Generated by Inventory Management System</p>
              <p>Report generated on ${getCurrentDate()}</p>
              <p style="margin-top:8px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</p>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Auto-trigger print dialog after content loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    };
  } else {
    alert("Please allow popups to export the report");
  }

  return printWindow;
};

export const exportComprehensivePurchaseReportToPDF = (data) => {
  const { purchases, payments, returns, summary, suppliers } = data;

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object" && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(numStr) || 0;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>📊 Comprehensive Purchase Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.6;
            color: #2c3e50;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 20px;
          }
          
          .container {
            background: white;
            border-radius: 15px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 30px 20px;
            position: relative;
          }
          
          .header h1 { 
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          
          .content {
            padding: 30px;
          }
          
          .summary-grid { 
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .summary-card { 
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 25px;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
            position: relative;
            overflow: hidden;
          }
          
          .summary-card.purchases { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
          .summary-card.payments { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
          .summary-card.returns { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
          .summary-card.outstanding { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); color: #2c3e50; }
          
          .summary-card h3 { 
            font-size: 14px;
            margin-bottom: 10px;
            opacity: 0.9;
          }
          
          .summary-card .amount { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 5px;
          }
          
          .summary-card .count { 
            font-size: 12px;
            opacity: 0.8;
          }
          
          .section { 
            margin: 40px 0;
            page-break-inside: avoid;
          }
          
          .section-header {
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            padding: 15px 20px;
            border-radius: 8px 8px 0 0;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .table-container {
            background: white;
            border-radius: 0 0 8px 8px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          table { 
            width: 100%;
            border-collapse: collapse;
          }
          
          th { 
            background: linear-gradient(90deg, #f8f9fa, #e9ecef);
            color: #495057;
            font-weight: 600;
            padding: 15px 12px;
            text-align: left;
            border-bottom: 2px solid #dee2e6;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td { 
            padding: 12px;
            border-bottom: 1px solid #f1f3f4;
            font-size: 11px;
            vertical-align: middle;
          }
          
          tr:nth-child(even) { 
            background: #fafbfc;
          }
          
          tr:hover { 
            background: #e3f2fd;
            transition: background-color 0.2s ease;
          }
          
          .summary-row { 
            background: linear-gradient(90deg, #667eea, #764ba2) !important;
            color: white !important;
            font-weight: 600;
          }
          
          .summary-row td {
            border-color: rgba(255,255,255,0.2);
            padding: 15px 12px;
          }
          
          .status-badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-paid { background: #d4edda; color: #155724; }
          .status-partial { background: #fff3cd; color: #856404; }
          .status-unpaid { background: #f8d7da; color: #721c24; }
          
          .amount-cell { 
            text-align: right;
            font-weight: 600;
            color: #28a745;
            font-family: 'Courier New', monospace;
          }
          
          .id-cell {
            font-weight: 600;
            color: #6c757d;
          }
          
          .footer { 
            margin-top: 50px;
            padding: 30px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 12px;
            text-align: center;
            color: #6c757d;
            border-top: 3px solid #667eea;
          }
          
          .footer-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .footer-stat {
            text-align: center;
          }
          
          .footer-stat .number {
            font-size: 18px;
            font-weight: 700;
            color: #495057;
            display: block;
          }
          
          .footer-stat .label {
            font-size: 11px;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          @media print {
            body { 
              background: white;
              padding: 0;
            }
            .container {
              box-shadow: none;
              border-radius: 0;
            }
            .section { 
              page-break-inside: avoid;
            }
            .summary-grid { 
              page-break-inside: avoid;
            }
          }
          
          @page {
            margin: 0.5in;
            size: A4;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 Comprehensive Purchase Report</h1>
            <p>Generated on ${getCurrentDate()}</p>
          </div>
          
          <div class="content">
            ${
              summary && Object.keys(summary).length > 0
                ? `
            <div class="summary-grid">
              <div class="summary-card purchases">
                <h3>🛒 Total Purchases</h3>
                <div class="amount">${formatCurrency(
                  summary.total_purchases?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_purchases?.count || 0
                } transactions</div>
              </div>
              <div class="summary-card payments">
                <h3>💳 Total Paid</h3>
                <div class="amount">${formatCurrency(
                  summary.total_paid || 0,
                )}</div>
                <div class="count">${
                  summary.total_payments?.count || 0
                } payments</div>
              </div>
              <div class="summary-card outstanding">
                <h3>⚠️ Outstanding</h3>
                <div class="amount">${formatCurrency(
                  summary.outstanding_amount || 0,
                )}</div>
                <div class="count">Remaining balance</div>
              </div>
              <div class="summary-card returns">
                <h3>↩️ Total Returns</h3>
                <div class="amount">${formatCurrency(
                  summary.total_returns?.amount || 0,
                )}</div>
                <div class="count">${
                  summary.total_returns?.count || 0
                } returns</div>
              </div>
            </div>
            `
                : ""
            }

            ${
              purchases && purchases.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                🛒 Purchase Transactions (${purchases.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 8%">ID</th>
                      <th style="width: 20%">Supplier</th>
                      <th style="width: 12%">Date</th>
                      <th style="width: 15%">Total Amount</th>
                      <th style="width: 15%">Amount Paid</th>
                      <th style="width: 15%">Remaining</th>
                      <th style="width: 8%">Items</th>
                      <th style="width: 7%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${purchases
                      .map((purchase) => {
                        const totalPrice = parseAmount(purchase.total_price);
                        const totalPaid = parseAmount(purchase.total_paid);
                        const remaining = parseAmount(
                          purchase.total_remained_amount,
                        );
                        const status =
                          remaining === 0
                            ? "Paid"
                            : totalPaid > 0
                              ? "Partial"
                              : "Unpaid";
                        const statusClass =
                          remaining === 0
                            ? "status-paid"
                            : totalPaid > 0
                              ? "status-partial"
                              : "status-unpaid";

                        return `
                        <tr>
                          <td class="id-cell">#${purchase.id}</td>
                          <td>${purchase.supplier_details?.name || "N/A"}</td>
                          <td>${formatDate(purchase.purchase_date)}</td>
                          <td class="amount-cell">${formatCurrency(
                            totalPrice,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            totalPaid,
                          )}</td>
                          <td class="amount-cell">${formatCurrency(
                            remaining,
                          )}</td>
                          <td style="text-align: center">${
                            purchase.purchase_items?.length || 0
                          }</td>
                          <td><span class="status-badge ${statusClass}">${status}</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="3"><strong>📊 TOTAL SUMMARY</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_purchases?.amount || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_paid || 0,
                      )}</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.outstanding_amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${purchases.reduce(
                        (sum, p) => sum + (p.purchase_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              payments && payments.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                💳 Payment Transactions (${payments.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Payment ID</th>
                      <th style="width: 15%">Purchase ID</th>
                      <th style="width: 20%">Amount</th>
                      <th style="width: 25%">Payment Date</th>
                      <th style="width: 15%">Currency</th>
                      <th style="width: 15%">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${payments
                      .map(
                        (payment) => `
                      <tr>
                        <td class="id-cell">#${payment.id}</td>
                        <td>Purchase #${payment.purchase}</td>
                        <td class="amount-cell">${formatCurrency(
                          parseAmount(payment.amount),
                        )}</td>
                        <td>${formatDate(payment.payment_date)}</td>
                        <td>${payment.currency_details?.title || "USD"}</td>
                        <td>${payment.payment_method || "Cash"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="2"><strong>💰 TOTAL PAYMENTS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_payments?.amount || 0,
                      )}</strong></td>
                      <td colspan="3"><strong>${
                        payments.length
                      } transactions</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            ${
              returns && returns.length > 0
                ? `
            <div class="section">
              <div class="section-header">
                ↩️ Return Transactions (${returns.length} items)
              </div>
              <div class="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 10%">Return ID</th>
                      <th style="width: 15%">Purchase ID</th>
                      <th style="width: 20%">Return Date</th>
                      <th style="width: 25%">Reason</th>
                      <th style="width: 15%">Refund Amount</th>
                      <th style="width: 10%">Items Count</th>
                      <th style="width: 5%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${returns
                      .map((returnItem) => {
                        const refundAmount = parseAmount(
                          returnItem.total_refund_amount ||
                            returnItem.return_items?.[0]?.refund_amount ||
                            0,
                        );
                        return `
                        <tr>
                          <td class="id-cell">#${returnItem.id}</td>
                          <td>Purchase #${
                            returnItem.purchase_details?.id ||
                            returnItem.purchase ||
                            "N/A"
                          }</td>
                          <td>${formatDate(returnItem.return_date)}</td>
                          <td>${returnItem.reason || "No reason provided"}</td>
                          <td class="amount-cell">${formatCurrency(
                            refundAmount,
                          )}</td>
                          <td style="text-align: center">${
                            returnItem.return_items?.length || 0
                          }</td>
                          <td><span class="status-badge status-paid">✓</span></td>
                        </tr>
                      `;
                      })
                      .join("")}
                    <tr class="summary-row">
                      <td colspan="4"><strong>🔄 TOTAL RETURNS</strong></td>
                      <td class="amount-cell"><strong>${formatCurrency(
                        summary.total_returns?.amount || 0,
                      )}</strong></td>
                      <td style="text-align: center"><strong>${returns.reduce(
                        (sum, r) => sum + (r.return_items?.length || 0),
                        0,
                      )}</strong></td>
                      <td><strong>-</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            `
                : ""
            }

            <div class="footer">
              <div class="footer-stats">
                <div class="footer-stat">
                  <span class="number">${purchases?.length || 0}</span>
                  <span class="label">Purchases</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${payments?.length || 0}</span>
                  <span class="label">Payments</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${returns?.length || 0}</span>
                  <span class="label">Returns</span>
                </div>
                <div class="footer-stat">
                  <span class="number">${formatCurrency(
                    summary.outstanding_amount || 0,
                  )}</span>
                  <span class="label">Outstanding</span>
                </div>
              </div>
              <p><strong>📊 Comprehensive Purchase Report</strong> • Generated by Inventory Management System</p>
              <p>Report generated on ${getCurrentDate()}</p>
              <p style="margin-top:8px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</p>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Auto-trigger print dialog after content loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    };
  } else {
    alert("Please allow popups to export the report");
  }

  return printWindow;
};

/**
 * Customer report: company info at top, then full table of customers with
 * previous balance, total sales, and current balance. Data from backend customer-report API.
 */
export const exportCustomerReportToPDF = (data) => {
  const customers = Array.isArray(data?.customers) ? data.customers : [];

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  };

  const parseNumericAmount = (value) => {
    if (value == null) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object") {
      const values = Object.values(value).map((item) => Number(item) || 0);
      return values.reduce((total, item) => total + item, 0);
    }
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-GB");
  };

  const formatBalance = (owed) => {
    if (!owed || typeof owed !== "object") return "—";
    const parts = Object.entries(owed).map(
      ([curr, amt]) => `${formatCurrency(amt)} ${curr}`,
    );
    return parts.length ? parts.join(" / ") : "—";
  };

  const getOpeningBalanceAmount = (customer) =>
    Number(customer?.opening_balance) || 0;

  const getCurrentBalanceAmount = (customer, summary) => {
    const outstanding = customer?.total_owed_by_currency ?? summary?.outstanding_amount;

    if (typeof outstanding === "number") return outstanding;
    if (typeof outstanding === "object" && outstanding !== null) {
      const values = Object.values(outstanding).map((value) => Number(value) || 0);
      return values.length
        ? values.reduce((total, value) => total + value, 0)
        : 0;
    }

    return (
      (Number(summary?.total_sales_amount) || 0) -
      (Number(summary?.total_payments_amount) || 0) -
      (Number(summary?.total_returns_amount) || 0)
    );
  };

  const getReturnItemsPaymentAmount = (item) => {
    const summaryAmount = Number(item?.summary?.total_returns_amount);
    if (!Number.isNaN(summaryAmount) && summaryAmount !== 0) return summaryAmount;

    return (item?.returns || []).reduce((total, returnItem) => {
      const returnTotal = Number(returnItem?.total_refound_amount);
      if (!Number.isNaN(returnTotal) && returnTotal !== 0) {
        return total + returnTotal;
      }

      const itemRefund = (returnItem?.return_items || []).reduce(
        (sum, childItem) =>
          sum +
          (Number(
            childItem?.refund_amount ??
              childItem?.calculated_refund_amount?.parsedValue ??
              childItem?.calculated_refund_amount,
          ) || 0),
        0,
      );

      return total + itemRefund;
    }, 0);
  };

  const getPaidPaymentsAmount = (item) => {
    const combinedSummaryAmount = parseNumericAmount(
      item?.summary?.total_paid_payments_amount,
    );
    if (combinedSummaryAmount !== 0) return combinedSummaryAmount;

    const summaryAmount = parseNumericAmount(item?.summary?.total_payments_amount);
    const balancePaymentsAmount = parseNumericAmount(
      item?.summary?.total_balance_payments_amount,
    );
    if (summaryAmount !== 0 || balancePaymentsAmount !== 0) {
      return summaryAmount + balancePaymentsAmount;
    }

    const paymentsTotal = (item?.payments || []).reduce(
      (total, payment) => total + parseNumericAmount(payment?.amount),
      0,
    );
    const balancePaymentsTotal = (item?.balance_payments || []).reduce(
      (total, payment) => total + parseNumericAmount(payment?.amount),
      0,
    );
    if (paymentsTotal !== 0 || balancePaymentsTotal !== 0) {
      return paymentsTotal + balancePaymentsTotal;
    }

    return (item?.sales || []).reduce(
      (total, sale) => total + parseNumericAmount(sale?.total_paid),
      0,
    );
  };

  const escapeHtml = (value) =>
    String(value ?? "—")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const getItemCost = (item) =>
    parseNumericAmount(
      item?.unit_cost ??
        item?.buy_price ??
        item?.purchase_details?.buy_price ??
        item?.product_details?.buy_price,
    );

  const getSaleItemRows = () => {
    const rows = [];
    customers.forEach((customerReport) => {
      const customer = customerReport.customer || customerReport;
      (customerReport.sales || []).forEach((sale) => {
        (sale.sale_items || []).forEach((item) => {
          const quantity = Number(item?.quantity) || 0;
          const salePrice = parseNumericAmount(item?.sale_price);
          const unitCost = getItemCost(item);
          const lineCost =
            parseNumericAmount(item?.line_cost) || quantity * unitCost;
          const lineTotal =
            parseNumericAmount(item?.line_total) || quantity * salePrice;
          const lineProfit =
            item?.line_profit !== undefined
              ? parseNumericAmount(item.line_profit)
              : lineTotal - lineCost;

          rows.push({
            billNo: sale?.bill_no || sale?.id || "—",
            date: sale?.sale_date || "—",
            customerName:
              sale?.customer_details?.name ||
              customer?.name ||
              "—",
            productName: item?.product_details?.name || "—",
            quantity,
            unit: item?.unit_details?.title || "—",
            currency:
              sale?.currency_details?.title ||
              sale?.currency_title ||
              customer?.opening_balance_currency_details?.title ||
              customer?.opening_balance_currency ||
              "AFG",
            salePrice,
            lineTotal,
          });
        });
      });
    });
    return rows;
  };

  const saleItemRows = getSaleItemRows();
  const saleItemsGrandTotal = saleItemRows.reduce(
    (total, row) => total + (Number(row.lineTotal) || 0),
    0,
  );
  const saleItemsCurrency = saleItemRows[0]?.currency || "AFG";
  const reportTotalAmount = customers.reduce(
    (total, customerReport) =>
      total + parseNumericAmount(customerReport?.summary?.total_sales_amount),
    0,
  );
  const reportTotalPaid = customers.reduce(
    (total, customerReport) =>
      total + parseNumericAmount(customerReport?.summary?.total_paid_payments_amount),
    0,
  );
  const reportTotalRemaining = customers.reduce(
    (total, customerReport) =>
      total + parseNumericAmount(customerReport?.summary?.outstanding_amount),
    0,
  );

  const balancePaymentRows = customers.flatMap((item) => {
    const paymentsList = Array.isArray(item.balance_payments)
      ? item.balance_payments
      : [];

    return paymentsList.map((payment) => ({
      id: payment?.id || "—",
      paymentDate: payment?.payment_date || payment?.created_at || payment?.date,
      customerName: payment?.customer_details?.name || item?.customer?.name || item?.name || "—",
      accountName: payment?.account_details?.name || payment?.account_name || "—",
      amount: parseNumericAmount(payment?.amount),
      currency:
        payment?.currency_details?.title ||
        payment?.currency_title ||
        item?.customer?.opening_balance_currency_details?.title ||
        item?.customer?.opening_balance_currency ||
        "AFG",
      exchangeRate: payment?.exchange_rate || "—",
      convertedAmount: parseNumericAmount(payment?.converted_amount || payment?.amount),
      notes: payment?.notes || "—",
    }));
  });

  // Same company info as sales print (Brotheran Qadari, contact, address)
  const companyName = "Habib Gilaman";
  const companyContact = "📞 0767080919 - 0767080919";
  const companyAddress = "🏢 , Kabul, Afghanistan";
  const companyLogo = `${window.location.origin}/logo.jpg`;

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Customer Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4 landscape; margin: 10mm; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 10px; line-height: 1.5; color: #2c3e50; background: #f5f5f5; padding: 20px; }
          .container { background: white; border-radius: 15px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); overflow: hidden; max-width: 1400px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; text-align: center; padding: 30px 20px; }
          .logo-wrap { width: 76px; height: 76px; margin: 0 auto 12px; border-radius: 14px; background: rgba(255,255,255,0.95); padding: 8px; box-shadow: 0 8px 20px rgba(0,0,0,0.18); display: flex; align-items: center; justify-content: center; }
          .logo-wrap img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
          .header h1 { font-size: 28px; font-weight: 700; margin-bottom: 8px; }
          .header .company-contact { font-size: 14px; opacity: 0.95; margin-bottom: 4px; }
          .header p { font-size: 14px; opacity: 0.9; }
          .content { padding: 30px; }
          .section-header { background: linear-gradient(90deg, #667eea, #764ba2); color: white; padding: 15px 20px; border-radius: 8px 8px 0 0; font-size: 16px; font-weight: 600; }
          .table-container { background: white; border-radius: 0 0 8px 8px; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
          table { width: 100%; border-collapse: collapse; }
          th { background: linear-gradient(90deg, #f8f9fa, #e9ecef); color: #495057; font-weight: 600; padding: 10px 8px; text-align: left; border-bottom: 2px solid #dee2e6; font-size: 9px; text-transform: uppercase; }
          td { padding: 8px; border-bottom: 1px solid #f1f3f4; font-size: 9px; vertical-align: top; }
          tr:nth-child(even) { background: #fafbfc; }
          .amount-cell { text-align: right; font-weight: 600; font-family: 'Courier New', monospace; }
          .detail-section { margin-top: 26px; break-inside: avoid; page-break-inside: avoid; }
          .profit-positive { color: #0f7b35; }
          .profit-negative { color: #c62828; }
          .footer { margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 12px; text-align: center; color: #6c757d; }
          @media print {
            body { background: #fff; padding: 0; }
            .container { box-shadow: none; border-radius: 0; max-width: none; }
            .header { padding: 20px 16px; }
            .content { padding: 18px; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo-wrap">
              <img src="${companyLogo}" alt="${escapeHtml(companyName)} Logo" onerror="this.parentNode.style.display='none';" />
            </div>
            <h1>${companyName}</h1>
            <div class="company-contact">${companyContact}</div>
            <div class="company-contact">${companyAddress}</div>
            <p>Customer Report</p>
            <p>Generated on ${getCurrentDate()}</p>
          </div>
          <div class="content">
            <div class="section-header">Sale Item Details (${saleItemRows.length} records)</div>
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th style="width: 8%">Bill No.</th>
                    <th style="width: 10%">Date</th>
                    <th style="width: 26%">Description</th>
                    <th style="width: 12%">Unit</th>
                    <th style="width: 8%">Qty</th>
                    <th style="width: 10%">Currency</th>
                    <th style="width: 12%">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${
                    saleItemRows.length
                      ? saleItemRows
                          .map(
                            (row) => `
                              <tr>
                                <td>${escapeHtml(row.billNo)}</td>
                                <td>${escapeHtml(formatDate(row.date))}</td>
                                <td>${escapeHtml(row.productName)}</td>
                                <td>${escapeHtml(row.unit)}</td>
                                <td class="amount-cell">${formatCurrency(row.quantity).replace(".00", "")}</td>
                                <td>${escapeHtml(row.currency)}</td>
                                <td class="amount-cell">${formatCurrency(row.lineTotal)} ${escapeHtml(row.currency)}</td>
                              </tr>
                            `,
                          )
                          .join("")
                      : `<tr><td colspan="7" style="text-align:center;">No sale item records found.</td></tr>`
                  }
                  <tr>
                    <td colspan="6" style="text-align:right;font-weight:700;">Total Amount</td>
                    <td class="amount-cell">${formatCurrency(reportTotalAmount || saleItemsGrandTotal)} ${escapeHtml(saleItemsCurrency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="detail-section">
              <div class="section-header">Summary</div>
              <div class="table-container">
                <table>
                  <tbody>
                    <tr>
                      <td style="font-weight:700;">Total Amount</td>
                      <td class="amount-cell">${formatCurrency(reportTotalAmount || saleItemsGrandTotal)} ${escapeHtml(saleItemsCurrency)}</td>
                    </tr>
                    <tr>
                      <td style="font-weight:700;">Total Paid</td>
                      <td class="amount-cell">${formatCurrency(reportTotalPaid)} ${escapeHtml(saleItemsCurrency)}</td>
                    </tr>
                    <tr>
                      <td style="font-weight:700;">Remaining</td>
                      <td class="amount-cell">${formatCurrency(reportTotalRemaining)} ${escapeHtml(saleItemsCurrency)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="footer">
              <p><strong>Customer Report</strong> • ${companyName}</p>
              <p>Report generated on ${getCurrentDate()}</p>
              <p style="margin-top:8px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</p>
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open(
    "",
    "_blank",
    "width=1200,height=800,scrollbars=yes",
  );
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 800);
    };
  } else {
    alert("Please allow popups to download the report");
  }
  return printWindow;
};
